import { createAction, props } from '@ngrx/store';
import {
	EnableLocationsModel,
	ExcludedLocationFilterModel,
	ExcludedLocationSearchModel,
	ListOptionModel,
	SearchResponse,
	FacilityModel,
	FacilitySearchFilterModel,
	FilterSearchRequest,
	NcpdpDetailsModel,
	CustomerDetailsModel,
	LocationNcpdpSearchFilterModel,
	NcpdpHistoryTransactionModel,
	LocationStatus340BSearchFilterModel,
	Status340BHistoryTransactionModel,
	FacilityOptionModel,
	NpiDetailsModel,
	MapLocationsModel,
	MapAllLocationsModel
} from '@twrx-api-models';
import { FacilityTableRecordModel } from 'app/models/dto/facility-table-record-model';
import { ActionRequestPayload } from 'app/store/action-request-payload';
import { ActionResponsePayload } from 'app/store/action-response-payload';
import { FetchCustomerFacilityRequest } from 'app/models/dto/fetch-customer-facility-request';

export enum FacilityActions {
	DeactivateFacility = '[Facility] DeactivateFacility',
	FacilityDeactivated = '[Facility] FacilityDeactivated',

	LoadCustomerFacility = '[Facility] LoadCustomerFacility',
	CustomerFacilityNotFound = '[Facility] CustomerFacilityNotFound',
	CustomerFacilityLoaded = '[Facility] CustomerFacilityLoaded',

	PreloadCustomerFacility = '[Facility] PreloadCustomerFacility',
	PreloadedCustomerFacilityNotFound = '[Facility] PreloadedCustomerFacilityNotFound',
	CustomerFacilityPreloaded = '[Facility] CustomerFacilityPreloaded',

	LoadCustomerFacilities = '[Facility] LoadCustomerFacilities',
	CustomerFacilitiesLoaded = '[Facility] CustomerFacilitiesLoaded',

	LoadExcludedLocations = '[Facility] LoadExcludedLocations',
	ExcludedLocationsLoaded = '[Facility] ExcludedLocationsLoaded',

	CreateCustomerFacility = '[Facility] CreateCustomerFacility',
	CustomerFacilityCreated = '[Facility] CustomerFacilityCreated',

	UpdateCustomerFacility = '[Facility] UpdateCustomerFacility',
	CustomerFacilityUpdated = '[Facility] CustomerFacilityUpdated',

	LoadFacilityOptions = '[Facility] LoadFacilityOptions',
	FacilityOptionsLoaded = '[Facility] FacilityOptionsLoaded',

	LoadLocationTagOptions = '[Facility] LoadLocationTagOptions',
	LocationTagOptionsLoaded = '[Facility] LocationTagOptionsLoaded',

	MapLocations = '[Facility] MapLocations',
	LocationMapped = '[Facility] LocationMapped',
	LocationsMapped = '[Facility] LocationsMapped',

	MapAllLocations = '[Facility] MapAllLocations',
	AllLocationsMapped = '[Facility] AllLocationsMapped',

	SetCurrentCustomer = '[Facility] SetCurrentCustomer',
	CurrentCustomerSet = '[Facility] CurrentCustomerSet',

	LoadNcpdpFacility = '[Facility] LoadNcpdpFacility',
	NcpdpFacilityLoaded = '[Facility] NcpdpFacilityLoaded',

	LoadNcpdpLocationHistory = '[Facility] LoadNcpdpLocationHistory',
	NcpdpLocationHistoryLoaded = '[Facility] NcpdpLocationHistoryLoaded',

	CheckNcpdpExcluded = '[Facility] CheckNcpdpExcluded',
	NcpdpExcludedChecked = '[Facility] NcpdpExcludedChecked',

	LoadStatus340BLocationHistory = '[Facility] LoadStatus340BLocationHistory',
	Status340BLocationHistoryLoaded = '[Facility] Status340BLocationHistoryLoaded',

	EnableLocations = '[Facility] EnableLocationsUpdate',
	LocationsEnabled = '[Facility] LocationsEnabled',
	LocationEnabled = '[Facility] LocationEnabled',

	StartLocationsFileGeneration = '[Facility] StartLocationsFileGeneration',
	LocationsFileGenerationStarted = '[Facility] LocationsFileGenerationStarted',

	LoadNpiDetails = '[Facility] LoadNpiDetails',
	NpiDetailsLoaded = '[Facility] NpiDetailsLoaded',
	LoadNpiDetailsFailed = '[Facility] LoadNpiDetailsFailed',

	ErrorOccurred = '[Facility] ErrorOccurred'
}

export const loadFacilityOptions = createAction(
	FacilityActions.LoadFacilityOptions,
	props<ActionRequestPayload<number>>()
);

export const facilityOptionsLoaded = createAction(
	FacilityActions.FacilityOptionsLoaded,
	props<ActionResponsePayload<FacilityOptionModel[]>>()
);

export const loadLocationTagOptions = createAction(
	FacilityActions.LoadLocationTagOptions,
	props<ActionRequestPayload<void>>()
);

export const locationTagOptionsLoaded = createAction(
	FacilityActions.LocationTagOptionsLoaded,
	props<ActionResponsePayload<ListOptionModel<number>[]>>()
);

export const loadMappingsOptions = createAction(
	FacilityActions.LoadFacilityOptions,
	props<ActionRequestPayload<number>>()
);

export const mappingsOptionsLoaded = createAction(
	FacilityActions.FacilityOptionsLoaded,
	props<ActionResponsePayload<ListOptionModel<number>[]>>()
);

export const preloadCustomerFacility = createAction(
	FacilityActions.PreloadCustomerFacility,
	props<ActionRequestPayload<FetchCustomerFacilityRequest>>()
);

export const preloadedCustomerFacilityNotFound = createAction(
	FacilityActions.PreloadedCustomerFacilityNotFound
);

export const customerFacilityPreloaded = createAction(
	FacilityActions.CustomerFacilityPreloaded,
	props<ActionResponsePayload<FacilityModel>>()
);

export const customerFacilityNotFound = createAction(
	FacilityActions.CustomerFacilityNotFound
);

export const loadCustomerFacilities = createAction(
	FacilityActions.LoadCustomerFacilities,
	props<
		ActionResponsePayload<FilterSearchRequest<FacilitySearchFilterModel>>
	>()
);

export const customerFacilitiesLoaded = createAction(
	FacilityActions.CustomerFacilitiesLoaded,
	props<ActionResponsePayload<SearchResponse<FacilityTableRecordModel>>>()
);

export const loadExcludedLocations = createAction(
	FacilityActions.LoadExcludedLocations,
	props<
		ActionResponsePayload<FilterSearchRequest<ExcludedLocationFilterModel>>
	>()
);

export const excludedLocationsLoaded = createAction(
	FacilityActions.ExcludedLocationsLoaded,
	props<ActionResponsePayload<SearchResponse<ExcludedLocationSearchModel>>>()
);

export const updateCustomerFacility = createAction(
	FacilityActions.UpdateCustomerFacility,
	props<ActionRequestPayload<FacilityModel>>()
);

export const customerFacilityUpdated = createAction(
	FacilityActions.CustomerFacilityUpdated
);

export const createCustomerFacility = createAction(
	FacilityActions.CreateCustomerFacility,
	props<ActionRequestPayload<FacilityModel>>()
);

export const customerFacilityCreated = createAction(
	FacilityActions.CustomerFacilityCreated
);

export const loadCustomerFacility = createAction(
	FacilityActions.LoadCustomerFacility,
	props<ActionRequestPayload<FetchCustomerFacilityRequest>>()
);

export const customerFacilityLoaded = createAction(
	FacilityActions.CustomerFacilityLoaded,
	props<ActionRequestPayload<FacilityModel>>()
);

export const deactivateFacility = createAction(
	FacilityActions.DeactivateFacility,
	props<ActionRequestPayload<number>>()
);

export const facilityDeactivated = createAction(
	FacilityActions.FacilityDeactivated
);

export const setCurrentCustomer = createAction(
	FacilityActions.SetCurrentCustomer,
	props<ActionRequestPayload<CustomerDetailsModel>>()
);

export const currentCustomerSet = createAction(
	FacilityActions.CurrentCustomerSet,
	props<ActionResponsePayload<CustomerDetailsModel>>()
);

export const loadNcpdpFacility = createAction(
	FacilityActions.LoadNcpdpFacility,
	props<ActionRequestPayload<string>>()
);

export const ncpdpFacilityLoaded = createAction(
	FacilityActions.NcpdpFacilityLoaded,
	props<ActionResponsePayload<NcpdpDetailsModel>>()
);

export const mapLocations = createAction(
	FacilityActions.MapLocations,
	props<ActionRequestPayload<MapLocationsModel>>()
);

export const locationMapped = createAction(FacilityActions.LocationMapped);

export const locationsMapped = createAction(FacilityActions.LocationsMapped);

export const mapAllLocations = createAction(
	FacilityActions.MapAllLocations,
	props<ActionRequestPayload<MapAllLocationsModel>>()
);

export const allLocationsMapped = createAction(
	FacilityActions.AllLocationsMapped
);

export const loadNcpdpLocationHistory = createAction(
	FacilityActions.LoadNcpdpLocationHistory,
	props<
		ActionRequestPayload<
			FilterSearchRequest<LocationNcpdpSearchFilterModel>
		>
	>()
);

export const ncpdpLocationHistoryLoaded = createAction(
	FacilityActions.NcpdpLocationHistoryLoaded,
	props<ActionResponsePayload<SearchResponse<NcpdpHistoryTransactionModel>>>()
);

export const checkNcpdpExcluded = createAction(
	FacilityActions.CheckNcpdpExcluded,
	props<ActionRequestPayload<string>>()
);

export const ncpdpExcludedChecked = createAction(
	FacilityActions.NcpdpExcludedChecked,
	props<ActionResponsePayload<boolean>>()
);

export const loadStatus340BLocationHistory = createAction(
	FacilityActions.LoadStatus340BLocationHistory,
	props<
		ActionRequestPayload<
			FilterSearchRequest<LocationStatus340BSearchFilterModel>
		>
	>()
);

export const startLocationsFileGeneration = createAction(
	FacilityActions.StartLocationsFileGeneration,
	props<ActionRequestPayload<number>>()
);

export const locationsFileGenerationStarted = createAction(
	FacilityActions.LocationsFileGenerationStarted
);

export const enableLocations = createAction(
	FacilityActions.EnableLocations,
	props<ActionResponsePayload<EnableLocationsModel>>()
);

export const locationsEnabled = createAction(FacilityActions.LocationsEnabled);

export const locationEnabled = createAction(FacilityActions.LocationEnabled);

export const status340BLocationHistoryLoaded = createAction(
	FacilityActions.Status340BLocationHistoryLoaded,
	props<
		ActionResponsePayload<SearchResponse<Status340BHistoryTransactionModel>>
	>()
);

export const loadNpiDetails = createAction(
	FacilityActions.LoadNpiDetails,
	props<ActionRequestPayload<string>>()
);

export const npiDetailsLoaded = createAction(
	FacilityActions.NpiDetailsLoaded,
	props<ActionResponsePayload<NpiDetailsModel>>()
);

export const loadNpiDetailsFailed = createAction(
	FacilityActions.LoadNpiDetailsFailed,
	props<ActionResponsePayload<NpiDetailsModel>>()
);

export const errorOccurred = createAction(FacilityActions.ErrorOccurred);
